import { useState } from 'react';
import {
  Sheet,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { DatePicker } from '../DatePicker';
import { Textarea } from '@/components/ui/textarea';
import { LucidePencil } from 'lucide-react';

export const InvoiceSelectDetailsSheet = ({
  onSave,
  customerNotes,
  dueDate,
  isLoading,
}: {
  customerNotes: string;
  dueDate: string;
  isLoading?: boolean;
  onSave: (customerNotes: string, dueDate: string) => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [editedCustomerNotes, setEditedCustomerNotes] = useState(customerNotes || '');
  const [editedDueDate, setEditedDueDate] = useState(dueDate || new Date().toISOString());

  const handleSave = async () => {
    setOpen(false);
    onSave(editedCustomerNotes, editedDueDate);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          className="absolute top-0 right-0"
          variant="ghost"
          onClick={() => setOpen(true)}
        >
          <LucidePencil className="w-4 h-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-lg font-semibold text-gray-900">
              Invoice Details
            </SheetTitle>
          </SheetHeader>

          <div className="overflow-y-auto px-6 py-6">
            <div className="flex flex-col gap-4">
              <div>
                <label className="label text-xs" htmlFor={`due-date`}>
                  Due Date
                </label>
                <DatePicker
                  date={new Date(editedDueDate)}
                  setDate={(date?: Date) =>
                    setEditedDueDate(date?.toISOString() || new Date().toISOString())
                  }
                  fromDate={new Date()}
                />
              </div>

              <div>
                <label className="label text-xs" htmlFor={`due-notes`}>
                  Customer Notes
                </label>

                <Textarea
                  value={editedCustomerNotes || ''}
                  onChange={(e) => setEditedCustomerNotes(e.target.value)}
                  placeholder="Add notes to the invoice..."
                  className="w-full p-3 border border-gray-300 rounded-md min-h-[100px] resize-y"
                />
              </div>
            </div>
          </div>

          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full justify-end gap-4">
              <SheetClose asChild>
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
              </SheetClose>
              <Button variant="default" size="sm" onClick={handleSave} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
